import React, { useEffect, useState } from 'react';
import {
    Flex, Typography, Tabs, DatePicker, Button, Table, Tag, Select,
    Form, Input, Modal, Popconfirm, Space, Tooltip
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axios from '../../axios/interceptor';

import {
    getStammdaten, updateStammdaten,
    getMappings, upsertMapping, deleteMapping,
    getExportLog, previewExport, runExport, setFreigabe,
} from './redux/slice';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DatevExport = () => {
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const { t } = useTranslation();
    const datev = useSelector(state => state.datevExport);

    const organizer = auth?.decodeToken?._id;

    const [period, setPeriod] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month'),
    ]);
    const [exportType, setExportType] = useState('full');
    const [selectedTimeIds, setSelectedTimeIds] = useState([]);

    // Stammdaten modal
    const [stammdatenModal, setStammdatenModal] = useState(false);
    const [stammdatenRecord, setStammdatenRecord] = useState(null);
    const [stammdatenForm] = Form.useForm();

    // Mapping modal
    const [mappingModal, setMappingModal] = useState(false);
    const [mappingRecord, setMappingRecord] = useState(null);
    const [mappingForm] = Form.useForm();

    useEffect(() => {
        if (!organizer) return;
        dispatch(getStammdaten(organizer));
        dispatch(getMappings(organizer));
        dispatch(getExportLog(organizer));
    }, [organizer]);

    const handlePreview = () => {
        if (!period[0] || !period[1]) return;
        dispatch(previewExport(
            organizer,
            period[0].format('YYYY-MM-DD'),
            period[1].format('YYYY-MM-DD')
        ));
    };

    const handleExport = () => {
        dispatch(runExport({
            organizer,
            periodStart: period[0].format('YYYY-MM-DD'),
            periodEnd: period[1].format('YYYY-MM-DD'),
            exportedBy: organizer,
            exportType,
        }));
    };

    const handleFreigabe = (status) => {
        if (!selectedTimeIds.length) return;
        dispatch(setFreigabe(
            selectedTimeIds, status, organizer,
            period[0].format('YYYY-MM-DD'),
            period[1].format('YYYY-MM-DD')
        ));
        setSelectedTimeIds([]);
    };

    const handleDownload = async (exportCharge, format = 'csv') => {
        try {
            const res = await axios.get('/datev/download', {
                params: { exportCharge, organizer, format },
                responseType: 'blob',
            });
            const ext = format === 'xlsx' ? 'xlsx' : 'csv';
            const mimeType = format === 'xlsx'
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'text/csv';
            const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `DATEV-Export-${exportCharge}.${ext}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error(err);
        }
    };

    // ── Stammdaten Tab ─────────────────────────────────────────────────────────
    const stammdatenColumns = [
        { title: 'Name', render: (_, r) => `${r.firstName} ${r.lastName}`, key: 'name' },
        { title: 'E-Mail', dataIndex: 'email', key: 'email' },
        { title: 'Personalnr.', dataIndex: 'personalnummer', key: 'personalnummer', render: v => v || <Text type='secondary'>—</Text> },
        { title: 'Abteilung', dataIndex: 'abteilung', key: 'abteilung', render: v => v || <Text type='secondary'>—</Text> },
        { title: 'Kostenstelle', dataIndex: 'kostenstelleStandard', key: 'kostenstelle', render: v => v || <Text type='secondary'>—</Text> },
        { title: 'Beschäftigungsart', dataIndex: 'beschaeftigungsart', key: 'beschaeftigungsart', render: v => v || <Text type='secondary'>—</Text> },
        {
            title: 'Aktion', key: 'action', render: (_, record) => (
                <Button size='small' onClick={() => {
                    setStammdatenRecord(record);
                    stammdatenForm.setFieldsValue({
                        personalnummer: record.personalnummer,
                        abteilung: record.abteilung,
                        kostenstelleStandard: record.kostenstelleStandard,
                        beschaeftigungsart: record.beschaeftigungsart,
                    });
                    setStammdatenModal(true);
                }}>Bearbeiten</Button>
            )
        },
    ];

    const handleStammdatenSave = () => {
        stammdatenForm.validateFields().then(values => {
            dispatch(updateStammdaten({ userId: stammdatenRecord._id, ...values }, organizer));
            setStammdatenModal(false);
        });
    };

    // ── Mapping Tab ────────────────────────────────────────────────────────────
    const mappingColumns = [
        { title: 'Interner Typ', dataIndex: 'internalType', key: 'internalType' },
        { title: 'Kategorie', dataIndex: 'category', key: 'category', render: v => v === 'timeEntry' ? 'Zeitbuchung' : 'Fehlzeit' },
        { title: 'Zeitart', dataIndex: 'zeitartCode', key: 'zeitartCode', render: v => v || '—' },
        { title: 'Lohnart (DATEV)', dataIndex: 'lohnartDatev', key: 'lohnartDatev', render: v => v || '—' },
        { title: 'Fehlzeitart', dataIndex: 'fehlzeitartCode', key: 'fehlzeitartCode', render: v => v || '—' },
        { title: 'Ausfallschlüssel', dataIndex: 'ausfallschluesselDatev', key: 'ausfallschluessel', render: v => v || '—' },
        { title: 'Entgeltfortzahlung', dataIndex: 'entgeltfortzahlung', key: 'entgelt', render: v => v ? 'Ja' : 'Nein' },
        {
            title: 'Aktion', key: 'action', render: (_, record) => (
                <Space>
                    <Button size='small' onClick={() => {
                        setMappingRecord(record);
                        mappingForm.setFieldsValue(record);
                        setMappingModal(true);
                    }}>Bearbeiten</Button>
                    <Popconfirm title='Mapping löschen?' onConfirm={() => dispatch(deleteMapping(record._id, organizer))}>
                        <Button size='small' danger>Löschen</Button>
                    </Popconfirm>
                </Space>
            )
        },
    ];

    const handleMappingSave = () => {
        mappingForm.validateFields().then(values => {
            dispatch(upsertMapping({ ...values, organizer, ...(mappingRecord?._id ? { _id: mappingRecord._id } : {}) }, organizer));
            setMappingModal(false);
            mappingForm.resetFields();
            setMappingRecord(null);
        });
    };

    // ── Preview Tab ────────────────────────────────────────────────────────────
    const freigabeColor = { offen: 'default', freigegeben: 'green', abgelehnt: 'red' };

    const timeColumns = [
        { title: 'Datum', dataIndex: 'date', key: 'date' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Personalnr.', dataIndex: 'personalnummer', key: 'personalnummer' },
        { title: 'Stunden', dataIndex: 'workedHours', key: 'workedHours' },
        { title: 'Kostenstelle', dataIndex: 'kostenstelle', key: 'kostenstelle' },
        {
            title: 'Freigabe', dataIndex: 'freigabeStatus', key: 'freigabeStatus',
            render: v => <Tag color={freigabeColor[v]}>{v}</Tag>
        },
        {
            title: 'Exportiert', dataIndex: 'exportiert', key: 'exportiert',
            render: v => <Tag color={v ? 'blue' : 'default'}>{v ? 'Ja' : 'Nein'}</Tag>
        },
    ];

    const absenceColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Personalnr.', dataIndex: 'personalnummer', key: 'personalnummer' },
        { title: 'Abwesenheitsart', dataIndex: 'leaveType', key: 'leaveType' },
        { title: 'Fehlzeitart', dataIndex: 'fehlzeitartCode', key: 'fehlzeitartCode', render: v => v || '—' },
        { title: 'Ausfallschlüssel', dataIndex: 'ausfallschluesselDatev', key: 'ausfallschluessel', render: v => v || '—' },
        { title: 'Tage', dataIndex: 'tage', key: 'tage' },
        { title: 'Stunden', dataIndex: 'stunden', key: 'stunden' },
        { title: 'Von', dataIndex: 'startDate', key: 'startDate' },
        { title: 'Bis', dataIndex: 'endDate', key: 'endDate' },
        {
            title: 'Exportiert', dataIndex: 'exportiert', key: 'exportiert',
            render: v => <Tag color={v ? 'blue' : 'default'}>{v ? 'Ja' : 'Nein'}</Tag>
        },
    ];

    // ── Export Log Tab ─────────────────────────────────────────────────────────
    const logColumns = [
        { title: 'Charge', dataIndex: 'exportCharge', key: 'exportCharge' },
        { title: 'Typ', dataIndex: 'exportType', key: 'exportType' },
        { title: 'Von', dataIndex: 'periodStart', key: 'periodStart', render: v => dayjs(v).format('DD.MM.YYYY') },
        { title: 'Bis', dataIndex: 'periodEnd', key: 'periodEnd', render: v => dayjs(v).format('DD.MM.YYYY') },
        { title: 'Datensätze', dataIndex: 'recordCount', key: 'recordCount' },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: v => {
                const colors = { completed: 'green', running: 'blue', error: 'red', corrected: 'orange' };
                return <Tag color={colors[v] || 'default'}>{v}</Tag>;
            }
        },
        { title: 'Exportiert von', key: 'exportedBy', render: (_, r) => r.exportedBy ? `${r.exportedBy.firstName} ${r.exportedBy.lastName}` : '—' },
        { title: 'Datum', dataIndex: 'createdAt', key: 'createdAt', render: v => dayjs(v).format('DD.MM.YYYY HH:mm') },
        {
            title: 'Download', key: 'download', render: (_, r) => (
                <Space>
                    <Button size='small' onClick={() => handleDownload(r.exportCharge, 'csv')}>CSV</Button>
                    <Button size='small' type='primary' onClick={() => handleDownload(r.exportCharge, 'xlsx')}>Excel</Button>
                </Space>
            )
        },
    ];

    const tabItems = [
        {
            key: 'preview',
            label: 'Export-Vorschau',
            children: (
                <Flex vertical gap={16}>
                    <Flex gap={12} wrap='wrap' align='center'>
                        <RangePicker
                            value={period}
                            onChange={setPeriod}
                            format='DD.MM.YYYY'
                        />
                        <Select value={exportType} onChange={setExportType} style={{ width: 160 }}>
                            <Select.Option value='full'>Vollständig</Select.Option>
                            <Select.Option value='timeTracking'>Nur Zeitbuchungen</Select.Option>
                            <Select.Option value='absence'>Nur Fehlzeiten</Select.Option>
                        </Select>
                        <Button onClick={handlePreview} loading={datev.loading}>Vorschau laden</Button>
                        {datev.previewData && (
                            <>
                                <Space>
                                    <Button onClick={() => handleFreigabe('freigegeben')} loading={datev.freigabeLoading} type='primary' disabled={!selectedTimeIds.length}>
                                        Freigeben ({selectedTimeIds.length})
                                    </Button>
                                    <Button onClick={() => handleFreigabe('abgelehnt')} loading={datev.freigabeLoading} danger disabled={!selectedTimeIds.length}>
                                        Ablehnen ({selectedTimeIds.length})
                                    </Button>
                                </Space>
                                <Popconfirm
                                    title={`${datev.previewData.totalTimeRecords + datev.previewData.totalAbsenceRecords} Datensätze exportieren?`}
                                    onConfirm={handleExport}
                                >
                                    <Button type='primary' loading={datev.exportLoading}>
                                        Export starten
                                    </Button>
                                </Popconfirm>
                            </>
                        )}
                    </Flex>

                    {datev.previewData && (
                        <>
                            <Title level={5}>
                                Zeitbuchungen ({datev.previewData.totalTimeRecords})
                                &nbsp;·&nbsp;
                                Fehlzeiten ({datev.previewData.totalAbsenceRecords})
                            </Title>
                            <Title level={5}>Zeitbuchungen</Title>
                            <Table
                                rowKey='_id'
                                dataSource={datev.previewData.timeRows}
                                columns={timeColumns}
                                loading={datev.loading}
                                rowSelection={{
                                    selectedRowKeys: selectedTimeIds,
                                    onChange: setSelectedTimeIds,
                                    getCheckboxProps: r => ({ disabled: r.exportiert }),
                                }}
                                pagination={{ pageSize: 20 }}
                                size='small'
                            />
                            <Title level={5}>Fehlzeiten</Title>
                            <Table
                                rowKey='_id'
                                dataSource={datev.previewData.absenceRows}
                                columns={absenceColumns}
                                loading={datev.loading}
                                pagination={{ pageSize: 20 }}
                                size='small'
                            />
                        </>
                    )}
                </Flex>
            ),
        },
        {
            key: 'stammdaten',
            label: 'Mitarbeiter-Stammdaten',
            children: (
                <Table
                    rowKey='_id'
                    dataSource={datev.stammdatenList}
                    columns={stammdatenColumns}
                    loading={datev.stammdatenLoading}
                    pagination={{ pageSize: 20 }}
                    size='small'
                />
            ),
        },
        {
            key: 'mapping',
            label: 'DATEV-Mapping',
            children: (
                <Flex vertical gap={12}>
                    <Button
                        type='primary'
                        style={{ width: 'fit-content' }}
                        onClick={() => {
                            setMappingRecord(null);
                            mappingForm.resetFields();
                            setMappingModal(true);
                        }}
                    >
                        Mapping hinzufügen
                    </Button>
                    <Table
                        rowKey='_id'
                        dataSource={datev.mappingList}
                        columns={mappingColumns}
                        loading={datev.mappingLoading}
                        pagination={{ pageSize: 20 }}
                        size='small'
                    />
                </Flex>
            ),
        },
        {
            key: 'log',
            label: 'Exportprotokoll',
            children: (
                <Table
                    rowKey='_id'
                    dataSource={datev.exportLogList}
                    columns={logColumns}
                    loading={datev.loading}
                    pagination={{ pageSize: 20 }}
                    size='small'
                    scroll={{ x: true }}
                />
            ),
        },
    ];

    return (
        <Flex vertical gap={16} className='padding-2'>
            <Title level={4} type='success'>DATEV Export</Title>
            <Tabs items={tabItems} defaultActiveKey='preview' />

            {/* Stammdaten Edit Modal */}
            <Modal
                title='Mitarbeiter-Stammdaten bearbeiten'
                open={stammdatenModal}
                onOk={handleStammdatenSave}
                onCancel={() => setStammdatenModal(false)}
                confirmLoading={datev.stammdatenLoading}
            >
                <Form form={stammdatenForm} layout='vertical'>
                    <Form.Item label='Personalnummer' name='personalnummer'>
                        <Input />
                    </Form.Item>
                    <Form.Item label='Abteilung' name='abteilung'>
                        <Input />
                    </Form.Item>
                    <Form.Item label='Kostenstelle (Standard)' name='kostenstelleStandard'>
                        <Input />
                    </Form.Item>
                    <Form.Item label='Beschäftigungsart' name='beschaeftigungsart'>
                        <Select allowClear>
                            <Select.Option value='Vollzeit'>Vollzeit</Select.Option>
                            <Select.Option value='Teilzeit'>Teilzeit</Select.Option>
                            <Select.Option value='Minijob'>Minijob</Select.Option>
                            <Select.Option value='Werkstudent'>Werkstudent</Select.Option>
                            <Select.Option value='Auszubildender'>Auszubildender</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Mapping Edit Modal */}
            <Modal
                title={mappingRecord ? 'Mapping bearbeiten' : 'Mapping hinzufügen'}
                open={mappingModal}
                onOk={handleMappingSave}
                onCancel={() => { setMappingModal(false); mappingForm.resetFields(); setMappingRecord(null); }}
                confirmLoading={datev.mappingLoading}
            >
                <Form form={mappingForm} layout='vertical'>
                    <Form.Item label='Interner Typ' name='internalType' rules={[{ required: true }]}>
                        <Input placeholder='z.B. sick, vacation, work' />
                    </Form.Item>
                    <Form.Item label='Kategorie' name='category' rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value='timeEntry'>Zeitbuchung</Select.Option>
                            <Select.Option value='absence'>Fehlzeit</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label='Zeitart-Code' name='zeitartCode'>
                        <Input placeholder='z.B. WORK, BREAK, TRAVEL' />
                    </Form.Item>
                    <Form.Item label='Lohnart (DATEV)' name='lohnartDatev'>
                        <Input placeholder='z.B. 100' />
                    </Form.Item>
                    <Form.Item label='Fehlzeitart-Code' name='fehlzeitartCode'>
                        <Input placeholder='z.B. URLAUB, KRANK' />
                    </Form.Item>
                    <Form.Item label='Ausfallschlüssel (DATEV)' name='ausfallschluesselDatev'>
                        <Input placeholder='z.B. 01' />
                    </Form.Item>
                    <Form.Item label='Entgeltfortzahlung' name='entgeltfortzahlung'>
                        <Select>
                            <Select.Option value={true}>Ja</Select.Option>
                            <Select.Option value={false}>Nein</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Flex>
    );
};

export default DatevExport;
